// src/pages/Projects/Projects.js
import React, { useEffect, useMemo, useState } from 'react';
import { getPageBySlug } from '../../utils/wordpressApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ProjectSlider from '../../components/ProjectSlider/ProjectSlider';
import { Link } from 'react-router-dom';
import './Projects.css';

const WP_PAGES_ENDPOINT =
  // Twój lokalny endpoint (taki jak w przykładach)
  `${window.location.origin}/wordpress/index.php?rest_route=/wp/v2/pages`;

async function fetchPageByTitle(titleExact) {
  // Szukamy po tytule, bierzemy najświeższą dopasowaną stronę
  const url = `${WP_PAGES_ENDPOINT}&search=${encodeURIComponent(titleExact)}&per_page=100`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('WP pages search failed');
  const list = await res.json();
  // dokładne dopasowanie po tytule, a potem fallback na zawieranie frazy
  const exact = list.find(p => (p?.title?.rendered || '').trim() === titleExact);
  if (exact) return exact;
  return list.find(p => (p?.title?.rendered || '').includes(titleExact)) || null;
}

// Funkcja do dynamicznego importowania obrazów
const importProjectImages = (projectId, maxImages = 20) => {
  const images = [];
  for (let i = 1; i <= maxImages; i++) {
    try {
      const image = require(`../../assets/images/projects/${projectId}/${i}.jpg`);
      images.push({ src: image });
    } catch (error) {
      // Przerywamy gdy nie ma więcej obrazów
      break;
    }
  }
  return images;
};

// Fallback dane - lokalne realizacje
const getFallbackProjects = () => {
  return [
    {
      title: "Komenda Wojewódzka PSP w Poznaniu",
      description: "Modernizacja instalacji teletechnicznych i elektrycznych w pomieszczeniach Komendy Wojewódzkiej Państwowej Straży Pożarnej w Poznaniu, ul. Wolnica 1",
      images: importProjectImages(7)
    },
    {
      title: "Hala magazynowa – Jasin",
      description: "Kompleksowe zaprojektowanie i wykonanie instalacji elektrycznej, bram napowietrzających oraz instalacji odgromowej dla hali magazynowej w Jasinie koło Poznania",
      images: importProjectImages(8)
    },
    {
      title: "Sygnalizacja pożaru i kontrola dostępu",
      description: "Ułożenie okablowania oraz montaż elementów sygnalizacji pożaru i kontroli dostępu dla inwestycji w Niepruszewie, ul. Świerkowa 5",
      images: importProjectImages(9)
    },
    {
      title: "Teatr Wielki",
      description: "Budowa oświetlenia scenicznego dla Teatr Wielki w Poznaniu 07.2022-06.2023",
      images: importProjectImages(1)
    },
    {
      title: "Instalacje podtynkowe",
      description: "STRUS Poznań Dębiec",
      images: importProjectImages(2)
    },
    {
      title: "Pekao Leasing",
      description: "",
      images: importProjectImages(3)
    },
    {
      title: "Instalacje podtynkowe",
      description: "Poznań ul. Na Miasteczku 3",
      images: importProjectImages(4)
    },
    {
      title: "Dukty światłowodowe",
      description: "Dukty światłowodowe dla Orange",
      images: importProjectImages(5)
    },
    {
      title: "Budowa FTTH",
      description: "",
      images: importProjectImages(6)
    }
  ]
    .filter(project => project.images.length > 0) // Filtrujemy tylko te z obrazami
    .map(project => ({
      ...project,
      images: project.images.map((img, i) => ({ ...img, alt: `${project.title} – zdjęcie ${i + 1}` }))
    }));
};

const Projects = () => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useLocalData, setUseLocalData] = useState(false);

  useEffect(() => {
    const fetchProjectsPage = async () => {
      try {
        setLoading(true);

        // 1) standardowy slug
        let projectsPage = await getPageBySlug('nasze-realizacje');

        // 2) fallback: slug z Twojego JSON-a (title: "Nasze realizacje", slug inny)
        if (!projectsPage) {
          projectsPage = await getPageBySlug('teatr-wielki-w-poznaniu-07-2022-06-2023');
        }

        if (!projectsPage) {
          console.warn('Nie znaleziono strony WordPress - używam lokalnych danych');
          setUseLocalData(true);
        } else {
          setPage(projectsPage);
        }
      } catch (err) {
        console.error('Error fetching projects page:', err);
        console.warn('Błąd połączenia z WordPress - używam lokalnych danych');
        setUseLocalData(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectsPage();
  }, []);

  // Parser: p (tytuł) -> [opcjonalnie p (opis, jeśli nie było jeszcze obrazów)] -> figure/img... -> p (nowy tytuł)
  const projects = useMemo(() => {
    // Jeśli używamy lokalnych danych, zwróć je
    if (useLocalData) {
      return getFallbackProjects();
    }

    const html = page?.content?.rendered;
    if (!html) return [];

    const root = document.createElement('div');
    root.innerHTML = html;

    const items = [];
    let current = null;
    let haveImages = false;

    const startProject = (titleText) => {
      current = { title: titleText, description: '', images: [] };
      haveImages = false;
    };

    const pushProjectIfValid = () => {
      if (current && current.title && current.images.length > 0) {
        items.push(current);
      }
      current = null;
      haveImages = false;
    };

    const collectImgsFromNode = (node, bucket) => {
      const imgs = node.tagName?.toLowerCase() === 'img'
        ? [node]
        : Array.from(node.querySelectorAll('img'));
      imgs.forEach((img) => {
        const src = img.getAttribute('src') || img.getAttribute('data-src') || '';
        if (!src) return;
        bucket.push({ src, alt: img.getAttribute('alt') || '' });
      });
    };

    const nodes = Array.from(root.children);
    nodes.forEach((node) => {
      const tag = (node.tagName || '').toLowerCase();

      if (tag === 'p' || tag === 'h2' || tag === 'h3') {
        const text = node.textContent?.trim() || '';
        if (!text) return;

        if (!current) {
          startProject(text);
          return;
        }

        if (!haveImages && !current.description) {
          current.description = text;
          return;
        }

        // Nowy tytuł po obrazach lub gdy opis już był
        pushProjectIfValid();
        startProject(text);
        return;
      }

      const containsImg = tag === 'img' || tag === 'figure' || node.querySelector?.('img');
      if (containsImg) {
        if (!current) return;
        collectImgsFromNode(node, current.images);
        haveImages = current.images.length > 0;
      }
    });

    // Ostatni projekt
    pushProjectIfValid();
    return items;
  }, [page?.content?.rendered, useLocalData]);

  if (loading) return <LoadingSpinner text="Ładowanie realizacji..." />;

  return (
    <div className="projects-page">
      <section className="projects-hero">
        <div className="projects-hero__content">
          <h1>{page?.title?.rendered || 'Nasze realizacje'}</h1>
          <p className="projects-hero__subtitle">
            Przykładowe wdrożenia — instalacje, oświetlenie, automatyka.
          </p>
          <div className="projects-hero__actions">
            <Link to="/kontakt" className="btn btn-primary">Poproś o wycenę</Link>
            <Link to="/oferta" className="btn btn-secondary">Zobacz ofertę</Link>
          </div>
        </div>
        <div className="projects-hero__decor">
          <div className="projects-hero__bubble projects-hero__bubble--a animate-float" />
          <div className="projects-hero__bubble projects-hero__bubble--b animate-float-delayed" />
          <div className="projects-hero__bubble projects-hero__bubble--c animate-float" />
        </div>
      </section>

      <section className="projects-list section">
        <div className="container">
          {projects.length === 0 ? (
            <div className="projects-empty card">
              <h3>Brak zdefiniowanych realizacji</h3>
              <p>W treści strony dodaj: tytuł (p), opcjonalny opis (p), 3–5 obrazów, potem kolejny tytuł itd.</p>
            </div>
          ) : (
            <div className="projects-grid">
              {projects.map((proj, idx) => (
                <article className="project-card animate-fade-in-up" key={`${proj.title}-${idx}`}>
                  <ProjectSlider images={proj.images} />
                  <div className="project-card__content">
                    <h2 className="project-card__title">{proj.title}</h2>
                    {proj.description && <p className="project-card__desc">{proj.description}</p>}
                  </div>
                </article>
              ))}
            </div>
          )}
          
        </div>
      </section>

      <section className="projects-cta">
        <div className="container">
          <div className="cta-content animate-fade-in-up">
            <h2>Masz podobny projekt?</h2>
            <p>Skontaktuj się z nami — doradzimy i przygotujemy ofertę.</p>
            <Link to="/kontakt" className="btn btn-primary btn-large">Skontaktuj się</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;